/** @type {Map<string, (el: HTMLElement) => void>} */
const mounts = new Map();

/** @type {Map<string, (el: HTMLElement) => void>} */
const unmounts = new Map();

/** @type {MutationObserver | null} */
let observer = null;

/**
 * Run callback for all existing and future elements matching selector.
 *
 * @param {string} selector - CSS selector to match elements
 * @param {(this: HTMLElement, el: HTMLElement) => void} callback - Called for each mount
 */
export function mount(selector, callback) {
    mounts.set(selector, callback);
    document.querySelectorAll(selector).forEach((el) => callback.call(el, el));
    initObserver();
}

/**
 * Run callback whenever an element matching selector is removed from DOM.
 *
 * @param {string} selector - CSS selector to match elements
 * @param {(this: HTMLElement, el: HTMLElement) => void} callback - Called for each unmount
 */
export function unmount(selector, callback) {
    unmounts.set(selector, callback);
    initObserver();
}

/**
 * Define a small component with mount/unmount lifecycle hooks.
 *
 * @param {string} selector - CSS selector to match elements
 * @param {{ mount?: (this: HTMLElement, el: HTMLElement) => void, unmount?: (this: HTMLElement, el: HTMLElement) => void }} def
 */
export function component(selector, def) {
    if (typeof def === 'function') {
        mount(selector, function () {
            const instance = new def(this);
            this.__componentInstance = instance;
            if (typeof instance.mount === 'function') {
                instance.mount();
            }
        });
        unmount(selector, function () {
            const instance = this.__componentInstance;
            if (instance && typeof instance.unmount === 'function') {
                instance.unmount();
            }
            delete this.__componentInstance;
        });
        return;
    }

    if (def.mount) mount(selector, def.mount);
    if (def.unmount) unmount(selector, def.unmount);
}

/**
 * Initializes the MutationObserver (only once).
 * @private
 */
function initObserver() {
    if (observer) return;

    observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
            for (const node of m.addedNodes) {
                if (!(node instanceof HTMLElement)) continue;
                mounts.forEach((cb, sel) => {
                    if (node.matches(sel)) cb.call(node, node);
                    node.querySelectorAll(sel).forEach((el) => cb.call(el, el));
                });
            }
            for (const node of m.removedNodes) {
                if (!(node instanceof HTMLElement)) continue;
                unmounts.forEach((cb, sel) => {
                    if (node.matches(sel)) cb.call(node, node);
                    node.querySelectorAll(sel).forEach((el) => cb.call(el, el));
                });
            }
        }
    });

    observer.observe(document.body, { childList: true, subtree: true });
}